import { useState, useEffect, useRef } from 'react';
import { categories, mainSiteUrl } from '@/mocks/categories';
import { useNavigate } from 'react-router-dom';

export default function Navbar() {
  const navigate = useNavigate();
  const [isScrolled, setIsScrolled] = useState(false);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [isMobileOpen, setIsMobileOpen] = useState(false);
  const [mobileDropdownOpen, setMobileDropdownOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 60);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setIsDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    if (isMobileOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => { document.body.style.overflow = ''; };
  }, [isMobileOpen]);

  return (
    <nav
      className={`fixed top-0 left-0 w-full z-50 transition-all duration-300 ${
        isScrolled
          ? 'bg-background-50/95 backdrop-blur-md shadow-sm'
          : 'bg-gradient-to-b from-black/60 via-black/30 to-transparent'
      }`}
    >
      <div className="w-full px-6 md:px-10 py-4 flex items-center justify-between">
        <a href="/" className="flex items-center gap-2 cursor-pointer">
          <span className="font-heading text-xl md:text-2xl font-bold whitespace-nowrap text-white drop-shadow-md">
            AllChinaBuy<span className="text-primary-400">Spreadsheet</span>
          </span>
        </a>

        <div className="hidden md:flex items-center gap-8">
          <a href="/" className="text-sm font-semibold whitespace-nowrap text-white drop-shadow-md hover:text-primary-300 transition-colors">
            Home
          </a>

          <a href="#categories" className="text-sm font-medium whitespace-nowrap text-white/90 drop-shadow-md hover:text-white transition-colors">
            Categories
          </a>

          <div ref={dropdownRef} className="relative">
            <button
              onClick={() => setIsDropdownOpen(!isDropdownOpen)}
              className="flex items-center gap-1 text-sm font-medium whitespace-nowrap text-white/90 drop-shadow-md hover:text-white transition-colors cursor-pointer"
            >
              Browse Products
              <div className="w-4 h-4 flex items-center justify-center">
                <i className={`ri-arrow-down-s-line transition-transform ${isDropdownOpen ? 'rotate-180' : ''}`}></i>
              </div>
            </button>

            {isDropdownOpen && (
              <div className="absolute top-full mt-2 left-0 bg-white rounded-xl shadow-xl border border-background-200 py-3 min-w-[240px] max-h-[420px] overflow-y-auto">
                {categories.map((cat) => (
                  <a
                    key={cat.name}
                    href={cat.url}
                    target="_blank"
                    rel="noopener nofollow"
                    className="flex items-center gap-3 px-4 py-2.5 text-sm text-foreground-700 hover:bg-primary-50 hover:text-primary-600 transition-colors whitespace-nowrap"
                    onClick={() => setIsDropdownOpen(false)}
                  >
                    <div className="w-8 h-8 rounded-lg bg-primary-50 flex items-center justify-center flex-shrink-0">
                      <i className={`${cat.icon} text-primary-500 text-sm`}></i>
                    </div>
                    {cat.name}
                  </a>
                ))}
              </div>
            )}
          </div>

          <a href="#faq" className="text-sm font-medium whitespace-nowrap text-white/90 drop-shadow-md hover:text-white transition-colors">
            FAQ
          </a>

          <button
            onClick={() => navigate('/allchinabuy-spreadsheet-guide')}
            className="text-sm font-semibold whitespace-nowrap text-white drop-shadow-md hover:text-primary-300 transition-colors cursor-pointer"
          >
            Shopping Guide
          </button>
        </div>

        <div className="flex items-center gap-3">
          <a
            href={mainSiteUrl}
            target="_blank"
            rel="noopener nofollow"
            className="hidden md:flex items-center gap-1.5 bg-primary-500 hover:bg-primary-600 text-white px-5 py-2.5 rounded-full text-sm font-semibold whitespace-nowrap transition-all cursor-pointer shadow-lg shadow-primary-500/25"
          >
            Buy It Now
            <div className="w-4 h-4 flex items-center justify-center">
              <i className="ri-arrow-right-up-line text-sm"></i>
            </div>
          </a>

          <button
            onClick={() => setIsMobileOpen(!isMobileOpen)}
            className="md:hidden w-10 h-10 flex items-center justify-center cursor-pointer text-white drop-shadow-md"
            aria-label="Toggle menu"
          >
            <div className="w-5 h-5 flex items-center justify-center">
              <i className={`text-xl ${isMobileOpen ? 'ri-close-line' : 'ri-menu-line'}`}></i>
            </div>
          </button>
        </div>
      </div>

      {isMobileOpen && (
        <div className="md:hidden bg-white border-t border-background-200 shadow-2xl animate-slide-down">
          <div className="px-5 py-4 flex flex-col gap-1 max-h-[75vh] overflow-y-auto">
            <a
              href="/"
              className="py-3 text-sm font-semibold text-foreground-900 hover:text-primary-500 transition-colors whitespace-nowrap flex items-center gap-2"
              onClick={() => setIsMobileOpen(false)}
            >
              <div className="w-5 h-5 flex items-center justify-center">
                <i className="ri-home-4-line text-base"></i>
              </div>
              Home
            </a>

            <a
              href="#categories"
              className="py-3 text-sm font-medium text-foreground-700 hover:text-primary-500 transition-colors whitespace-nowrap flex items-center gap-2"
              onClick={() => setIsMobileOpen(false)}
            >
              <div className="w-5 h-5 flex items-center justify-center">
                <i className="ri-apps-2-line text-base"></i>
              </div>
              Categories
            </a>

            <div>
              <button
                onClick={() => setMobileDropdownOpen(!mobileDropdownOpen)}
                className="w-full flex items-center justify-between py-3 text-sm font-medium text-foreground-700 hover:text-primary-500 transition-colors cursor-pointer"
              >
                <span className="flex items-center gap-2">
                  <div className="w-5 h-5 flex items-center justify-center">
                    <i className="ri-store-2-line text-base"></i>
                  </div>
                  Browse Products
                </span>
                <div className="w-4 h-4 flex items-center justify-center">
                  <i className={`ri-arrow-down-s-line transition-transform ${mobileDropdownOpen ? 'rotate-180' : ''}`}></i>
                </div>
              </button>

              {mobileDropdownOpen && (
                <div className="pl-5 border-l-2 border-primary-200 ml-3 space-y-0.5 mt-1 mb-2">
                  {categories.map((cat) => (
                    <a
                      key={cat.name}
                      href={cat.url}
                      target="_blank"
                      rel="noopener nofollow"
                      className="flex items-center gap-3 py-2.5 text-xs text-foreground-600 hover:text-primary-500 transition-colors whitespace-nowrap"
                      onClick={() => { setIsMobileOpen(false); setMobileDropdownOpen(false); }}
                    >
                      <div className="w-7 h-7 rounded-md bg-primary-50 flex items-center justify-center flex-shrink-0">
                        <i className={`${cat.icon} text-primary-500 text-xs`}></i>
                      </div>
                      {cat.name}
                    </a>
                  ))}
                </div>
              )}
            </div>

            <a
              href="#faq"
              className="py-3 text-sm font-medium text-foreground-700 hover:text-primary-500 transition-colors whitespace-nowrap flex items-center gap-2"
              onClick={() => setIsMobileOpen(false)}
            >
              <div className="w-5 h-5 flex items-center justify-center">
                <i className="ri-question-line text-base"></i>
              </div>
              FAQ
            </a>

            <button
              onClick={() => { navigate('/allchinabuy-spreadsheet-guide'); setIsMobileOpen(false); }}
              className="py-3 text-sm font-semibold text-foreground-700 hover:text-primary-500 transition-colors whitespace-nowrap flex items-center gap-2 cursor-pointer"
            >
              <div className="w-5 h-5 flex items-center justify-center">
                <i className="ri-book-open-line text-base"></i>
              </div>
              Shopping Guide
            </button>

            <a
              href={mainSiteUrl}
              target="_blank"
              rel="noopener nofollow"
              className="mt-2 flex items-center justify-center gap-1.5 bg-primary-500 hover:bg-primary-600 text-white px-5 py-3 rounded-full text-sm font-semibold whitespace-nowrap transition-all cursor-pointer"
              onClick={() => setIsMobileOpen(false)}
            >
              Buy It Now
              <div className="w-4 h-4 flex items-center justify-center">
                <i className="ri-arrow-right-up-line text-sm"></i>
              </div>
            </a>
          </div>
        </div>
      )}
    </nav>
  );
}